export function tilesRowsSignature(rows: unknown[]): string {
  return rows
    .map((row, index) => {
      const r = row as Record<string, unknown>
      const fields = (r?.fields as Record<string, unknown> | undefined) ?? r
      const id =
        (fields?._id as { value?: unknown } | undefined)?.value ??
        (fields?.document as { _id?: { value?: unknown } } | undefined)?._id?.value ??
        index
      return `${index}:${String(id)}`
    })
    .join('|')
}

/**
 * Извлекает стабильный набор row-id для сравнения двух источников tiles.
 *
 * Приоритет идентификатора:
 * 1) `row.fields._id.value`
 * 2) `row.fields.document._id.value`
 * 3) fallback на индекс строки
 */
export function extractTilesRowIds(rows: unknown[]): Set<string> {
  const ids = new Set<string>()
  for (const [index, row] of rows.entries()) {
    const r = row as Record<string, unknown>
    const fields = (r?.fields as Record<string, unknown> | undefined) ?? r
    const id =
      (fields?._id as { value?: unknown } | undefined)?.value ??
      (fields?.document as { _id?: { value?: unknown } } | undefined)?._id?.value ??
      index
    ids.add(String(id))
  }
  return ids
}

/**
 * Выбирает актуальные строки tiles при рассинхроне `pageFields` и `registry/model`.
 *
 * Основные ветки:
 * - синхронные данные: предпочитаем `modelRows` (более рендер-ориентированный источник);
 * - add: `modelRows` может опережать `pageRows` -> выбираем `modelRows`;
 * - delete/reloadPanel: `pageRows` уже обновлены, `modelRows` stale -> выбираем `pageRows`;
 * - fallback: сравнение по длине.
 */
export function resolveTilesRowsFromSources(
  pageRows: unknown[],
  modelRows: unknown[],
): unknown[] {
  const pageSig = tilesRowsSignature(pageRows)
  const modelSig = tilesRowsSignature(modelRows)

  if (pageSig === modelSig && pageRows.length === modelRows.length) {
    if (modelRows.length > 0) return modelRows
    if (pageRows.length > 0) return pageRows
    return []
  }

  if (pageRows.length !== modelRows.length) {
    const pageIds = extractTilesRowIds(pageRows)
    const modelIds = extractTilesRowIds(modelRows)
    const onlyInModel = [...modelIds].filter((id) => !pageIds.has(id))
    const onlyInPage = [...pageIds].filter((id) => !modelIds.has(id))

    // Delete / reloadPanel: pageFields уже без удалённой строки, registry/model ещё stale.
    if (
      onlyInModel.length > 0 &&
      onlyInPage.length === 0 &&
      pageRows.length < modelRows.length &&
      pageRows.length >= 2
    ) {
      return pageRows
    }

    // Add: registry/model опережает pageFields (одна новая строка).
    if (
      onlyInModel.length > 0 &&
      onlyInPage.length === 0 &&
      modelRows.length > pageRows.length
    ) {
      return modelRows
    }

    // ReloadPanel / server ahead: pageFields длиннее registry.
    if (
      onlyInPage.length > 0 &&
      onlyInModel.length === 0 &&
      pageRows.length > modelRows.length
    ) {
      return pageRows
    }

    return pageRows.length >= modelRows.length ? pageRows : modelRows
  }

  return pageRows.length > 0 ? pageRows : modelRows
}
